/**
 * Fabrique du « backend » vu par l'application : un `SentinelleApi` (REST), la
 * surface `/admin/**` et une fabrique de `StreamClient` (temps réel).
 *
 * `createBackend()` ne parle qu'à l'**API réelle** — en développement comme en
 * production, `BackendProvider` l'utilise toujours (aucun commutateur de mode
 * démonstration). La couche `src/mocks/**` subsiste mais n'est plus chargée
 * par l'application : elle ne sert plus qu'à simuler le backend dans les
 * tests, qui injectent leur propre `Backend` (voir `createMockedBackend`).
 */

import { AdminApi } from "./api/adminClient";
import { SentinelleApi } from "./api/client";
import { HttpClient } from "./api/http";
import { StreamClient, defaultEventSourceTransport } from "./api/stream";
import type { StreamTopic } from "./contract/types";
import { adminSessionStore } from "./auth/adminSession";
import { sessionStore } from "./auth/session";
import { emitUnauthorized } from "./auth/appGate";
import type { MockBackend } from "./mocks";

export interface Backend {
  api: SentinelleApi;
  /** Surface `/admin/**` — régime d'identité distinct (jeton admin). */
  adminApi: AdminApi;
  /** Ouvre un flux temps réel pour un réseau et une liste de sujets. */
  createStream(networkId: string, topics: StreamTopic[]): StreamClient;
  readonly usesMocks: boolean;
  /** Présent uniquement en mode mocks (développement) — scénarios de démo. */
  readonly mock: MockBackend | null;
  dispose(): void;
}

export interface CreateBackendOptions {
  baseUrl?: string;
}

export function createBackend(options: CreateBackendOptions = {}): Backend {
  const baseUrl = options.baseUrl ?? import.meta.env.VITE_API_BASE_URL ?? "";
  const transport = defaultEventSourceTransport();

  const http = new HttpClient({
    baseUrl,
    getAuthToken: () => sessionStore.getToken(),
    onUnauthorized: emitUnauthorized,
  });
  const api = new SentinelleApi(http);

  // Client admin : même base, mais résout le **jeton admin**.
  const adminHttp = new HttpClient({
    baseUrl,
    getAuthToken: () => adminSessionStore.getToken(),
    onUnauthorized: emitUnauthorized,
  });
  const adminApi = new AdminApi(adminHttp);

  const streams = new Set<StreamClient>();

  return {
    api,
    adminApi,
    usesMocks: false,
    mock: null,
    createStream(networkId, topics) {
      const url = http.url(`/networks/${encodeURIComponent(networkId)}/stream`, { topics });
      const client = new StreamClient({ url, topics, transport });
      streams.add(client);
      return client;
    },
    dispose() {
      for (const client of streams) client.stop();
      streams.clear();
    },
  };
}
